import { StepNavigationButton } from "@client/components/StepNavigationButton";
import { NotesContentViewer } from "@client/components/round/allocator/MarkdownViewer";
import { ProseDiff } from "@client/components/admin-tools/ProseDiff";
import {
  useActingAsUserId,
  useEffectiveIsSfcAdmin,
  useRound,
  useUserProfiles,
} from "@client/pages/round/round-context";
import { trpc } from "@client/trpc";
import { richTextToMultilineText } from "@client/utils/isRichTextBlank";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  Text,
  EmptyMessage,
  Button,
  Dialog,
  ScrollableDialogContent,
  DialogBody,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  formatMediumDateTime,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Switch,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@sfc/ui";
import { CheckIcon, CopyIcon, HistoryIcon } from "lucide-react";
import { useState } from "react";

type NoteHistoryTarget = {
  roundId: string;
  memberId: string;
  /** null for the round-level general notes. */
  evaluableId: string | null;
  field: "notes" | "positive" | "negative" | "general";
};

/**
 * Small history icon that opens a modal for browsing a note's saved versions
 * (one per editing session). Recommenders see this only on their own notes;
 * round and SFC admins see it on every note. The server independently enforces
 * the same restriction.
 */
export function NoteHistoryButton({
  target,
  noteLabel,
}: {
  target: NoteHistoryTarget;
  /** Modal title context, e.g. "GW — PauseAI_Global". */
  noteLabel: string;
}) {
  const round = useRound();
  const userProfiles = useUserProfiles();
  const effectiveIsSfcAdmin = useEffectiveIsSfcAdmin();
  const actingAsUserId = useActingAsUserId();
  const [open, setOpen] = useState(false);
  const isAdmin = userIsAdmin(actingAsUserId, {
    round,
    userProfiles,
    effectiveIsSfcAdmin,
  });

  if (target.memberId !== actingAsUserId && !isAdmin) {
    return null;
  }

  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              variant="ghost"
              size="icon-sm"
              className="size-6 text-muted-foreground hover:text-foreground"
              aria-label="View note history"
              onClick={(e) => {
                // Notes render inside collapsibles/linkable regions; keep the
                // click from toggling those.
                e.stopPropagation();
                setOpen(true);
              }}
            />
          }
        >
          <HistoryIcon className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent>Note history</TooltipContent>
      </Tooltip>
      <Dialog open={open} onOpenChange={setOpen}>
        <ScrollableDialogContent className="grid h-[48rem] grid-rows-[auto_minmax(0,1fr)] sm:max-w-5xl">
          {open && (
            <NoteHistoryDialogBody target={target} noteLabel={noteLabel} />
          )}
        </ScrollableDialogContent>
      </Dialog>
    </>
  );
}

function NoteHistoryDialogBody({
  target,
  noteLabel,
}: {
  target: NoteHistoryTarget;
  noteLabel: string;
}) {
  const { data, isLoading } = trpc.getNoteVersionHistory.useQuery(target);
  const sessions = data?.sessions ?? [];
  // null = follow the latest session/edit until the user navigates.
  const [viewedSessionIndex, setViewedSessionIndex] = useState<number | null>(
    null,
  );
  const [viewedEditIndex, setViewedEditIndex] = useState<number | null>(null);
  const [compareWithCurrent, setCompareWithCurrent] = useState(false);
  const [copied, setCopied] = useState(false);

  const sessionIndex = viewedSessionIndex ?? sessions.length - 1;
  const session = sessions[sessionIndex];
  const editIndex = viewedEditIndex ?? (session?.edits.length ?? 1) - 1;
  const edit = session?.edits[editIndex];
  const latestSession = sessions[sessions.length - 1];
  const current = latestSession?.edits[latestSession.edits.length - 1];
  const viewingCurrent = edit?.version === current?.version;

  const selectSession = (nextIndex: number) => {
    const nextSession = sessions[nextIndex];
    setViewedSessionIndex(nextIndex);
    setViewedEditIndex((nextSession?.edits.length ?? 1) - 1);
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Note history — {noteLabel}</DialogTitle>
        <DialogDescription>
          Browse editing sessions and the individual autosaves within them.
        </DialogDescription>
      </DialogHeader>
      {isLoading ? (
        <div className="flex min-h-0 items-center justify-center">
          <Spinner className="size-5" />
        </div>
      ) : sessions.length === 0 ? (
        <EmptyMessage variant="plain" className="min-h-0 py-6">
          No saved versions of this note yet.
        </EmptyMessage>
      ) : (
        <div className="flex min-h-0 flex-col gap-3">
          <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Editing session</Label>
              <div className="flex gap-1">
                <StepNavigationButton
                  direction="previous"
                  iconOnly
                  label="Previous editing session"
                  disabled={sessionIndex <= 0}
                  onClick={() => selectSession(sessionIndex - 1)}
                />
                <Select
                  value={String(sessionIndex)}
                  onValueChange={(value) =>
                    value != null && selectSession(Number(value))
                  }
                >
                  <SelectTrigger className="min-w-0 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sessions.map((item, index) => (
                      <SelectItem
                        key={`${item.start}-${index}`}
                        value={String(index)}
                      >
                        Session {index + 1} of {sessions.length} ·{" "}
                        {item.edits.length}{" "}
                        {item.edits.length === 1 ? "edit" : "edits"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <StepNavigationButton
                  direction="next"
                  iconOnly
                  label="Next editing session"
                  disabled={sessionIndex >= sessions.length - 1}
                  onClick={() => selectSession(sessionIndex + 1)}
                />
              </div>
              {session && (
                <Text size="xs" tone="muted-foreground">
                  {formatMediumDateTime(session.start)}–
                  {formatMediumDateTime(session.end)}
                </Text>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Autosaved edit within session</Label>
              <div className="flex gap-1">
                <StepNavigationButton
                  direction="previous"
                  iconOnly
                  label="Previous autosaved edit"
                  disabled={editIndex <= 0}
                  onClick={() => setViewedEditIndex(editIndex - 1)}
                />
                <Select
                  value={String(editIndex)}
                  onValueChange={(value) =>
                    value != null && setViewedEditIndex(Number(value))
                  }
                >
                  <SelectTrigger className="min-w-0 flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {session?.edits.map((item, index) => (
                      <SelectItem
                        key={`${item.version}-${index}`}
                        value={String(index)}
                      >
                        Edit {index + 1} of {session.edits.length}
                        {item.flagged ? " · Flagged" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <StepNavigationButton
                  direction="next"
                  iconOnly
                  label="Next autosaved edit"
                  disabled={editIndex >= (session?.edits.length ?? 1) - 1}
                  onClick={() => setViewedEditIndex(editIndex + 1)}
                />
              </div>
              {edit && (
                <Text size="xs" tone="muted-foreground">
                  {formatMediumDateTime(edit.timestamp)}
                  {viewingCurrent ? " · Current state" : ""}
                  {edit.flagged ? " · Flagged for review" : ""}
                </Text>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Switch
                id="compare-note-with-current"
                checked={compareWithCurrent}
                onCheckedChange={setCompareWithCurrent}
              />
              <Label htmlFor="compare-note-with-current">
                Compare with current
              </Label>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!edit) {
                  return;
                }

                void navigator.clipboard.writeText(
                  richTextToMultilineText(edit.text),
                );
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? (
                <CheckIcon className="size-3.5" />
              ) : (
                <CopyIcon className="size-3.5" />
              )}
              {copied ? "Selected edit copied" : "Copy selected edit"}
            </Button>
          </div>
          <DialogBody className="rounded-md border p-4">
            {edit && current && compareWithCurrent && !viewingCurrent ? (
              <ProseDiff
                oldText={richTextToMultilineText(edit.text)}
                newText={richTextToMultilineText(current.text)}
                layout="side-by-side"
                oldLabel="Selected edit"
                newLabel="Current state"
              />
            ) : edit && current && compareWithCurrent ? (
              <Text size="sm" tone="muted-foreground">
                This edit is the current state, so there are no differences.
              </Text>
            ) : (
              edit && <NotesContentViewer value={edit.text} />
            )}
          </DialogBody>
        </div>
      )}
    </>
  );
}
